import { streamQuantityEventName } from '@/components/commonComponents/ExportByColumnModal';
import {
  AmiliHealthOrderStatus,
  OrderStatus,
  PhysicalCondition,
  TemperatureCondition,
} from '@/enums';
import {
  CreateOrderEntity,
  OrderEntity,
  OrderItemEntity,
  OrderParams,
  OrderStatusEntity,
  UpdateOrderEntity,
} from '@/pages/privatePages/order/types';
import { newQueryKeys } from '@/queries/queryKeys';
import { QueryFunctionContext } from '@tanstack/react-query';
import APIService from './axios.service';
import { BaseService } from './base.service';

/**
 * The server stores a PRODUCT-type (composite) order item as a parent
 * priced 0 plus one child item per priced component (`parentOrderItemId`
 * = the parent's id; quantity = component qty × parent qty; price fixed at
 * save time). Everywhere in the app the order should read as what was
 * ordered, so the children are folded into their parent:
 * - children are removed from `orderItems` (kept on the parent's
 *   `componentOrderItems`);
 * - the parent's unit `price` becomes Σ child price × child quantity ÷
 *   parent quantity - i.e. what one unit of it actually costs.
 * Orders without children come back unchanged.
 */
export const collapseComponentChildOrderItems = (
  order: OrderEntity,
): OrderEntity => {
  const orderItems = order?.orderItems;
  if (!orderItems?.some(item => item.parentOrderItemId)) return order;

  const childrenByParentId = new Map<string, OrderItemEntity[]>();
  for (const item of orderItems) {
    if (!item.parentOrderItemId) continue;
    const siblings = childrenByParentId.get(item.parentOrderItemId) ?? [];
    siblings.push(item);
    childrenByParentId.set(item.parentOrderItemId, siblings);
  }

  // Children are always billable leaves, but sum any nesting just in case.
  const getChildrenTotal = (parentId: string): number =>
    (childrenByParentId.get(parentId) ?? []).reduce(
      (total, child) =>
        total +
        (childrenByParentId.has(child.id)
          ? getChildrenTotal(child.id)
          : Number(child.price || 0) * Number(child.quantity || 0)),
      0,
    );

  return {
    ...order,
    orderItems: orderItems
      .filter(item => !item.parentOrderItemId)
      .map(item => {
        const componentOrderItems = childrenByParentId.get(item.id);
        if (!componentOrderItems) return item;

        const quantity = Number(item.quantity || 0);
        const unitPrice = quantity ? getChildrenTotal(item.id) / quantity : 0;

        return {
          ...item,
          componentOrderItems,
          price: Number(unitPrice.toFixed(2)),
          // A PRODUCT item's kits / FMT bottles live on its children -
          // surface them on the item too, so order-level views (list rows,
          // kit export, mark kits received) still see them. The Edit form
          // reads them per child (componentOrderItems) instead.
          kits: [
            ...(item.kits ?? []),
            ...componentOrderItems.flatMap(child => child.kits ?? []),
          ],
          kitSerials: [
            ...(item.kitSerials ?? []),
            ...componentOrderItems.flatMap(child => child.kitSerials ?? []),
          ],
          fmtBottles: [
            ...(item.fmtBottles ?? []),
            ...componentOrderItems.flatMap(child => child.fmtBottles ?? []),
          ],
        };
      }),
  };
};

export interface OrderCountParams {
  channelIds: string[];
  startDate?: string;
  endDate?: string;
  orderStatuses?: (OrderStatus | AmiliHealthOrderStatus)[];
}

export type RegenerateInvoiceDocumentsResponse = {
  // true only when this call created a new Xero invoice.
  isInvoiceCreated: boolean;
  // null right after a new invoice whose PDF upload failed - call again.
  invoiceUrl: string | null;
  deliveryOrderPdfUrl: string | null;
};

class OrderServiceClass extends BaseService<
  'order',
  OrderEntity,
  CreateOrderEntity
> {
  constructor() {
    super('orders');
  }

  // Order reads with PRODUCT-type child items folded into their parents
  // (collapseComponentChildOrderItems) - use these, not the base
  // get/getInfinite/getById, as a query's queryFn.
  getCollapsed = async (context: Parameters<typeof this.get>[0]) => {
    const response = await this.get(context);
    return {
      ...response,
      data: response?.data?.map(collapseComponentChildOrderItems),
    };
  };

  getInfiniteCollapsed = async (
    context: Parameters<typeof this.getInfinite>[0],
  ) => {
    const response = await this.getInfinite(context);
    return {
      ...response,
      data: response?.data?.map(collapseComponentChildOrderItems),
    };
  };

  getByIdCollapsed = async (context: Parameters<typeof this.getById>[0]) =>
    collapseComponentChildOrderItems(await this.getById(context));

  createOrder = (payload: CreateOrderEntity): Promise<OrderEntity[]> => {
    return APIService.post(this.path, payload);
  };

  updateOrder = (payload: UpdateOrderEntity): Promise<OrderEntity> => {
    const { id, ...rest } = payload;

    return APIService.put(this.path + '/' + id, rest);
  };

  // Creates the order's Xero invoice if it has none, else re-validates it,
  // and fills a missing invoice / delivery order PDF. Never creates a second
  // invoice, changes status or emails the customer.
  regenerateInvoiceDocuments = (
    id: string,
  ): Promise<RegenerateInvoiceDocumentsResponse> => {
    return APIService.post(
      this.path + '/' + id + '/regenerate-invoice-documents',
    );
  };

  streamOrder = (params: OrderParams): Promise<any> => {
    return APIService.get(this.path + '/stream-orders', {
      params,
      responseType: 'stream',
      onDownloadProgress(data) {
        const responseData = data.event?.target?.response || '';
        // Regex to find all progress values
        const regex = /"progress"\s*:\s*(\d+(\.\d+)?)/g;
        const matches = [...responseData.matchAll(regex)];
        // Get the last match
        const lastMatch = matches[matches.length - 1];
        const lastProgress = lastMatch
          ? Math.round(parseFloat(lastMatch[1]))
          : 0;

        // Dispatch kit quantity for percent progress bar
        window.dispatchEvent(
          new CustomEvent(streamQuantityEventName, {
            detail: { percentProgress: lastProgress },
          }),
        );
      },
    });
  };

  getOrderHistory(
    context: QueryFunctionContext<
      ReturnType<(typeof newQueryKeys)['order']['list']>
    >,
  ): Promise<OrderStatusEntity[]> {
    const { params } = context.queryKey[0];
    return APIService.get(`orders/${params?.id}/history`);
  }

  updateMultipleOrdersStatus = (payload: {
    ids?: string[];
    orderStatus: string;
    orderIds?: string[];
    currentStatus?: string;
    orderConditionOnReceived?: {
      temperature: TemperatureCondition;
      physical: PhysicalCondition;
    };
  }): Promise<unknown> => {
    return APIService.patch(this.path + `/multiple-order-status`, payload);
  };

  count = (params: OrderCountParams): Promise<number> => {
    return APIService.get(this.path + '/count', {
      params,
    });
  };
}
export const orderService = new OrderServiceClass();
